import React, { useState, useEffect, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom';
import toast from 'react-hot-toast';
import { apiCall } from '../services/api';

interface DownloadRecord {
  type: 'file' | 'zip';
  timestamp: string;
  bucketName: string;
  key?: string;
  fileName?: string;
  fileSize?: number;
  fileCount?: number;
  totalSize?: number;
  items?: string[];
}

interface DownloadHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
}

function formatBytes(bytes: number): string {
  if (!bytes || bytes === 0) return '—';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

function formatDate(iso: string): { date: string; time: string } {
  const d = new Date(iso);
  return {
    date: d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }),
    time: d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })
  };
}

function getFileExt(name: string): string {
  return name.includes('.') ? (name.split('.').pop()?.toLowerCase() || '') : '';
}

function getExtBadgeClass(ext: string): string {
  if (['jpg','jpeg','png','gif','svg','webp'].includes(ext)) return 'bg-pink-100 text-pink-600';
  if (['pdf'].includes(ext)) return 'bg-red-100 text-red-600';
  if (['doc','docx'].includes(ext)) return 'bg-blue-100 text-blue-600';
  if (['xls','xlsx','csv'].includes(ext)) return 'bg-green-100 text-green-600';
  if (['zip','rar','7z','tar','gz'].includes(ext)) return 'bg-yellow-100 text-yellow-700';
  if (['mp4','mov','avi','mkv'].includes(ext)) return 'bg-purple-100 text-purple-600';
  return 'bg-gray-100 text-gray-500';
}

const DownloadHistoryModal: React.FC<DownloadHistoryModalProps> = ({ isOpen, onClose }) => {
  const [downloads, setDownloads] = useState<DownloadRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [days, setDays] = useState(30);
  const [totalSize, setTotalSize] = useState(0);
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [sortKey, setSortKey] = useState<string>('timestamp');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const fetchHistory = useCallback(async (d: number) => {
    setLoading(true);
    setExpandedRows(new Set());
    try {
      const res = await apiCall(`/s3/my-downloads?days=${d}`);
      const items: DownloadRecord[] = res.downloads || [];
      setDownloads(items);
      const sum = items.reduce((acc: number, u: DownloadRecord) => acc + (u.fileSize || u.totalSize || 0), 0);
      setTotalSize(sum);
    } catch (err) {
      console.error('Failed to load download history:', err);
      toast.error('Failed to load download history');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) { fetchHistory(days); setPage(1); setSearchTerm(''); }
  }, [isOpen, days, fetchHistory]);

  const toggleRow = (idx: number) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      next.has(idx) ? next.delete(idx) : next.add(idx);
      return next;
    });
  };

  const filtered = useMemo(() => {
    if (!searchTerm) return downloads;
    const q = searchTerm.toLowerCase();
    return downloads.filter(d =>
      (d.bucketName || '').toLowerCase().includes(q) ||
      (d.fileName || '').toLowerCase().includes(q) ||
      (d.key || '').toLowerCase().includes(q) ||
      (d.items || []).some(i => i.toLowerCase().includes(q))
    );
  }, [downloads, searchTerm]);

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      let aVal: any, bVal: any;
      if (sortKey === 'timestamp') { aVal = a.timestamp; bVal = b.timestamp; }
      else if (sortKey === 'bucketName') { aVal = a.bucketName; bVal = b.bucketName; }
      else if (sortKey === 'fileName') { aVal = a.fileName || ''; bVal = b.fileName || ''; }
      else if (sortKey === 'fileSize') { aVal = a.fileSize || a.totalSize || 0; bVal = b.fileSize || b.totalSize || 0; }
      else if (sortKey === 'type') { aVal = a.type; bVal = b.type; }
      else { aVal = ''; bVal = ''; }
      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filtered, sortKey, sortDir]);

  const totalPages = Math.ceil(sorted.length / pageSize);
  const paginated = sorted.slice((page - 1) * pageSize, page * pageSize);

  const handleSort = (key: string) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };

  if (!isOpen) return null;
  const dayOptions = [7, 30, 60, 90];

  const SortIcon = ({ col }: { col: string }) => (
    <svg className={`h-3 w-3 ml-0.5 inline ${sortKey === col ? 'text-gray-900' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      {sortKey === col && sortDir === 'desc'
        ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />}
    </svg>
  );

  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-5xl w-full max-h-[85vh] shadow-2xl flex flex-col" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center shadow-md shadow-purple-200">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
              </svg>
            </div>
            <div>
              <h3 className="text-base font-semibold text-gray-900">Download History</h3>
              <p className="text-xs text-gray-500">
                {downloads.length} download{downloads.length !== 1 ? 's' : ''} · {formatBytes(totalSize)} total
              </p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1.5 rounded-lg hover:bg-gray-100 transition-colors" aria-label="Close">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Date range pills */}
        <div className="px-5 py-2 border-b border-gray-100 bg-gray-50/50 flex items-center space-x-2">
          <span className="text-xs text-gray-500 font-medium">Period:</span>
          {dayOptions.map(d => (
            <button key={d} onClick={() => { setDays(d); setPage(1); }}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${
                days === d ? 'bg-purple-600 text-white shadow-sm'
                  : 'bg-white text-gray-600 border border-gray-200 hover:border-purple-300 hover:text-purple-600'
              }`}>{d} days</button>
          ))}
        </div>

        {/* Search */}
        <div className="px-5 py-2.5 border-b border-gray-200">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input type="text" value={searchTerm}
              onChange={e => { setSearchTerm(e.target.value); setPage(1); }}
              className="block w-full pl-9 pr-3 py-1.5 text-sm border border-gray-300 rounded-md bg-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
              placeholder="Search by file name, bucket, folder..." />
          </div>
        </div>

        {/* Table */}
        <div className="flex-1 min-h-0 overflow-auto" style={{ maxHeight: 'calc(85vh - 220px)' }}>
          {loading ? (
            <div className="flex justify-center items-center h-48">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
              <span className="ml-3 text-sm text-gray-600">Loading...</span>
            </div>
          ) : paginated.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <svg className="h-10 w-10 text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
              </svg>
              <p className="text-sm text-gray-500">No downloads found in the last {days} days</p>
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="w-7 px-2 py-2"></th>
                  <th className="px-3 py-2 text-left text-[11px] font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('timestamp')}>
                    Date & Time <SortIcon col="timestamp" />
                  </th>
                  <th className="px-3 py-2 text-left text-[11px] font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('bucketName')}>
                    Bucket <SortIcon col="bucketName" />
                  </th>
                  <th className="px-3 py-2 text-left text-[11px] font-medium text-gray-500 uppercase tracking-wider">Directory</th>
                  <th className="px-3 py-2 text-left text-[11px] font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('fileName')}>
                    File Name <SortIcon col="fileName" />
                  </th>
                  <th className="px-3 py-2 text-right text-[11px] font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('fileSize')}>
                    Size <SortIcon col="fileSize" />
                  </th>
                  <th className="px-3 py-2 text-center text-[11px] font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('type')}>
                    Type <SortIcon col="type" />
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {paginated.map((dl, idx) => {
                  const globalIdx = (page - 1) * pageSize + idx;
                  const isExpanded = expandedRows.has(globalIdx);
                  const isBatch = dl.type === 'zip';
                  const hasItems = isBatch && dl.items && dl.items.length > 0;
                  const { date, time } = formatDate(dl.timestamp);
                  const fullKey = dl.key || '';
                  const folder = fullKey.includes('/') ? fullKey.substring(0, fullKey.lastIndexOf('/') + 1) : '/';

                  return (
                    <React.Fragment key={globalIdx}>
                      <tr className={`hover:bg-gray-50 transition-colors ${hasItems ? 'cursor-pointer' : ''} ${isExpanded ? 'bg-purple-50/40' : ''}`}
                        onClick={() => hasItems && toggleRow(globalIdx)}>
                        <td className="px-2 py-1.5 text-center">
                          {hasItems ? (
                            <svg className={`w-3.5 h-3.5 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          ) : <span className="w-3.5 h-3.5 inline-block"></span>}
                        </td>
                        <td className="px-3 py-1.5 whitespace-nowrap">
                          <div className="flex items-center space-x-1.5">
                            <div className={`flex-shrink-0 w-1.5 h-1.5 rounded-full ${isBatch ? 'bg-amber-400' : 'bg-purple-400'}`}></div>
                            <span className="text-xs text-gray-700">{date} <span className="text-gray-400">{time}</span></span>
                          </div>
                        </td>
                        <td className="px-3 py-1.5 whitespace-nowrap">
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-blue-50 text-blue-700 border border-blue-200">{dl.bucketName}</span>
                        </td>
                        <td className="px-3 py-1.5">
                          <div className="flex items-center text-xs text-gray-600 max-w-[150px]" title={folder}>
                            <svg className="w-3.5 h-3.5 text-amber-500 mr-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
                            </svg>
                            <span className="truncate">{isBatch ? '(multiple)' : (folder || '/')}</span>
                          </div>
                        </td>
                        <td className="px-3 py-1.5">
                          {isBatch ? (
                            <div className="flex items-center text-xs">
                              <span className="inline-flex items-center justify-center w-6 h-6 rounded mr-2 flex-shrink-0 text-[10px] font-bold bg-yellow-100 text-yellow-700">ZIP</span>
                              <span className="font-medium text-gray-900">Batch download ({dl.fileCount} file{(dl.fileCount || 0) !== 1 ? 's' : ''})</span>
                              {hasItems && <span className="ml-1.5 text-[10px] text-purple-500">{isExpanded ? 'collapse' : 'expand'}</span>}
                            </div>
                          ) : (() => {
                            const name = dl.fileName || (dl.key ? dl.key.split('/').pop() : '') || '—';
                            const ext = getFileExt(name);
                            return (
                              <div className="flex items-center text-xs max-w-[220px]" title={name}>
                                <span className={`inline-flex items-center justify-center w-6 h-6 rounded mr-2 flex-shrink-0 text-[10px] font-bold ${getExtBadgeClass(ext)}`}>
                                  {ext ? ext.substring(0, 3).toUpperCase() : '—'}
                                </span>
                                <span className="truncate font-medium text-gray-900">{name}</span>
                              </div>
                            );
                          })()}
                        </td>
                        <td className="px-3 py-1.5 whitespace-nowrap text-right">
                          <span className="text-xs font-medium text-gray-700">{formatBytes(dl.fileSize || dl.totalSize || 0)}</span>
                        </td>
                        <td className="px-3 py-1.5 whitespace-nowrap text-center">
                          <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium ${
                            isBatch ? 'bg-amber-50 text-amber-700 border border-amber-200' : 'bg-purple-50 text-purple-700 border border-purple-200'
                          }`}>{isBatch ? '📦 Batch' : '📄 File'}</span>
                        </td>
                      </tr>

                      {/* Expanded sub-rows */}
                      {isExpanded && hasItems && dl.items!.map((itemKey, subIdx) => {
                        const itemName = itemKey.split('/').pop() || itemKey;
                        const itemFolder = itemKey.includes('/') ? itemKey.substring(0, itemKey.lastIndexOf('/') + 1) : '/';
                        const ext = getFileExt(itemName);
                        return (
                          <tr key={`${globalIdx}-${subIdx}`} className="bg-purple-50/30 border-l-4 border-l-purple-200">
                            <td className="px-2 py-1"></td>
                            <td className="px-3 py-1"><span className="text-[10px] text-gray-400 pl-3">↳ file {subIdx + 1}</span></td>
                            <td className="px-3 py-1"></td>
                            <td className="px-3 py-1">
                              <div className="flex items-center text-[11px] text-gray-500 max-w-[150px]" title={itemFolder}>
                                <svg className="w-3 h-3 text-amber-400 mr-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                  <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
                                </svg>
                                <span className="truncate">{itemFolder}</span>
                              </div>
                            </td>
                            <td className="px-3 py-1">
                              <div className="flex items-center text-[11px] max-w-[220px]" title={itemName}>
                                <span className={`inline-flex items-center justify-center w-5 h-5 rounded mr-1.5 flex-shrink-0 text-[9px] font-bold ${getExtBadgeClass(ext)}`}>
                                  {ext ? ext.substring(0, 3).toUpperCase() : '—'}
                                </span>
                                <span className="truncate text-gray-700">{itemName}</span>
                              </div>
                            </td>
                            <td className="px-3 py-1 text-right text-[10px] text-gray-400">—</td>
                            <td className="px-3 py-1"></td>
                          </tr>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination footer */}
        {totalPages > 0 && (
          <div className="px-5 py-2 border-t border-gray-200 bg-gray-50 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center space-x-3">
              <p className="text-xs text-gray-600">
                Showing <span className="font-medium">{(page - 1) * pageSize + 1}</span>–<span className="font-medium">{Math.min(page * pageSize, sorted.length)}</span> of <span className="font-medium">{sorted.length}</span>
              </p>
              <div className="flex items-center space-x-1.5">
                <label className="text-xs text-gray-600">Show:</label>
                <select value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}
                  className="border border-gray-300 rounded text-xs px-1.5 py-0.5 bg-white focus:ring-1 focus:ring-purple-500 focus:border-purple-500">
                  {[10, 20, 30, 50].map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                <span className="text-xs text-gray-600">per page</span>
              </div>
            </div>
            <div className="flex items-center space-x-1.5">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                className="px-2.5 py-1 border border-gray-300 rounded text-xs bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">
                Previous
              </button>
              <span className="text-xs text-gray-600">Page {page} of {totalPages}</span>
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                className="px-2.5 py-1 border border-gray-300 rounded text-xs bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  , document.body);
};

export default DownloadHistoryModal;
